import useQueryParams from "utils/useQueryParams";
import { useEffect, useRef, useState } from "react";
import { useAnalytics } from "JS/analyticsAPI";
import "Styles/pages/analytics/memberActivity.css";
function Records({ member, params }) {
  const [cursor, setCursor] = useState(undefined);
  const [batches, setBatches] = useState([]);
  const request = useAnalytics(`members/${member.id}/activity`, {
    ...params, cursor, limit: 100,
  });
  useEffect(() => {
    if (request.data) setBatches(previous => [
      ...previous.filter(batch => batch.cursor !== request.data.pagination.next_cursor),
      { cursor: request.data.pagination.next_cursor, data: request.data },
    ]);
  }, [request.data]);
  return (
    <div className="ma-record-stack">
      <h3>방문별 행동 기록</h3>
      {request.error && (
        <p role="alert">
          {request.error}
          <button onClick={request.retry}>다시 시도</button>
        </p>
      )}
      {batches.map(batch => (
        <div key={batch.cursor || 'first'}>
          {batch.data.visits.days.map(day => (
            <section key={day.date}>
              <h4>{day.date_label} · 방문 {day.visit_count}회</h4>
              {day.items.map(visit => (
                <section className="ma-visit" key={visit.session_id}>
                  <header>
                    <strong>{visit.pages[0]?.time_label}</strong>
                    <span>{visit.device === "mobile" ? "모바일" : "PC"}</span>
                  </header>
                  <ol className="ma-visit-pages">
                    {visit.pages.map(p => (
                      <li key={p.page_view_id}>
                        <div className="ma-page-heading">
                          <strong>{p.page_title || p.page_path} 페이지 방문</strong>
                          <small>{p.engagement_label}</small>
                        </div>
                        <small>{p.time_label}</small>
                        {!!p.actions.length && (
                          <ul className="ma-page-actions">
                            {p.actions.map(e => (
                              <li key={e.event_id} className={e.kind === "error" ? "ma-action-error" : e.kind === "purchase" ? "ma-action-paid" : ""}>
                                <span>{e.time_label} · {e.label}</span>
                                {e.amount != null && <b>₩{e.amount.toLocaleString("ko-KR")}</b>}
                              </li>
                            ))}
                          </ul>
                        )}
                        {p.is_last_page && <p className="ma-visit-end">이 방문의 마지막 관찰 페이지</p>}
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </section>
          ))}
        </div>
      ))}
      {batches.some(batch => batch.data.unlinked.days.length > 0) && (
        <section className="ma-unlinked">
          <h3>방문에 연결되지 않은 기록</h3>
          {batches.map(batch => batch.data.unlinked.days.map(day => (
            <section key={day.date}>
              <h4>{day.date_label} · {day.event_count}건</h4>
              {day.items.map(e => <p key={e.event_id}>{e.time_label} · {e.label}</p>)}
            </section>
          )))}
        </section>
      )}
      {request.loading && <p role="status">활동 기록 조회 중…</p>}
      {!request.loading && !request.error && !batches.some(batch => batch.data.visits.days.length || batch.data.unlinked.days.length) && (
        <p>선택한 날짜에 관찰된 기록이 없습니다.</p>
      )}
      {request.data?.pagination.has_more && (
        <button disabled={request.loading} onClick={() => setCursor(request.data.pagination.next_cursor)}>
          더 불러오기
        </button>
      )}
    </div>
  );
}
export function ActivityRecord({
  member,
  days,
  preview = false,
  asOf,
  observationDays,
}) {
  const {searchParams, updateQueryParams} = useQueryParams();
  const date = key => {
    const value = searchParams.get(key) || '';
    return /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value ? value : '';
  };
  const from = date('from'), to = date('to');
  const invalid = !preview && Boolean(from && to && from > to);
  const params = {
    as_of: asOf,
    observation_days: observationDays || (Number.isFinite(days) ? days : 7),
    mode: preview ? "preview" : "all",
    ...(!preview
      ? {
          from: from || undefined,
          to: to || undefined,
        }
      : {}),
  };
  return (
    <div className="ma-record">
      <div className="ma-date">
        {preview ? (
          <p>마지막 관찰일 기준 최근 7일</p>
        ) : (
          <>
            <label>
              시작일
              <input
                type="date"
                aria-label="기록 시작일"
                value={from}
                max={to || undefined}
                aria-invalid={invalid}
                onChange={(e) => updateQueryParams({from: e.target.value})}
              />
            </label>
            <label>
              종료일
              <input
                type="date"
                aria-label="기록 종료일"
                value={to}
                min={from || undefined}
                aria-invalid={invalid}
                onChange={(e) => updateQueryParams({to: e.target.value})}
              />
            </label>
            <button
              onClick={() => {
                updateQueryParams({from: null, to: null});
              }}
            >
              전체 기간
            </button>
          </>
        )}
      </div>
      {invalid ? (
        <p role="alert">
          시작일은 종료일보다 늦을 수 없습니다. 날짜를 다시 선택해 주세요.
        </p>
      ) : (
        <Records
          key={member.id + JSON.stringify(params)}
          member={member}
          params={params}
        />
      )}
      <p className="ma-note">
        마지막 기록만으로 이탈 원인을 판단하지 않습니다.
      </p>
    </div>
  );
}
export function MemberActivity({
  list,
  days,
  page,
  setPage,
  pagination,
  pending,
  onExpand,
  asOf,
}) {
  const {searchParams, updateQueryParams} = useQueryParams();
  const selected = searchParams.get("member");
  const preview = useRef(null);
  const active = list.find((m) => m.id === selected) || list[0];
  return (
    <div className="ma-layout">
      <div className="ma-list" aria-label="회원 선택">
        {list.map((m) => (
          <button
            key={m.id}
            className={
              "ma-member " + (m.id === active?.id ? "ma-selected" : "")
            }
            aria-pressed={m.id === active?.id}
            aria-controls="member-activity-preview"
            onClick={() => {
              updateQueryParams({member: m.id});
              if (window.matchMedia?.("(max-width:850px)").matches)
                preview.current?.scrollIntoView({
                  block: "start",
                });
            }}
          >
            <span className="ma-member-title">
              <b>{m.name}</b>
              <span className="ma-action">
                {m.id === active?.id ? "행동 보는 중" : "행동 보기"} →
              </span>
            </span>
            <span className="ma-meta">
              {m.channel} · {m.joined} 가입
            </span>
            <span className="ma-status">
              {pending ? "관찰 중 · " : ""}
              {m.state}
              {m.has_error && <em>오류 기록</em>}
            </span>
            <span className="ma-last">
              최근 행동 · {m.last_action?.label || "관찰 없음"}
            </span>
          </button>
        ))}
        {!list.length && (
          <p className="a2-empty">조건에 맞는 회원이 없습니다.</p>
        )}
        {pagination.total > 0 && (
          <div className="a2-pagination">
            <span>회원 {pagination.total}명</span>
            <div>
              <button
                aria-label="이전 페이지"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                ←
              </button>
              <span>
                {page} / {pagination.total_pages}
              </span>
              <button
                aria-label="다음 페이지"
                disabled={page >= pagination.total_pages}
                onClick={() => setPage(page + 1)}
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>
      <section
        ref={preview}
        className="ma-preview"
        id="member-activity-preview"
        aria-label="선택 회원 행동 기록"
      >
        {active ? (
          <>
            <div className="ma-preview-head">
              <div>
                <h3>{active.name}의 행동 기록</h3>
                <p>
                  {active.id} · {pending ? "관찰 중" : `가입 후 ${days}일`} ·{" "}
                  {active.channel}
                </p>
              </div>
              <button className="ma-expand" onClick={() => onExpand(active)}>
                자세히 보기
              </button>
            </div>
            <ActivityRecord
              key={active.id}
              member={active}
              days={days}
              asOf={asOf}
              preview
            />
          </>
        ) : (
          <p className="a2-empty">
            필터나 검색어를 변경하면 회원 행동을 확인할 수 있습니다.
          </p>
        )}
      </section>
    </div>
  );
}
